import { useEffect, useMemo, useState, type CSSProperties, type SyntheticEvent } from "react";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCorners,
  pointerWithin,
  rectIntersection,
  type CollisionDetection,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  User,
  Bot,
  Clock,
  Link2,
  FileText,
  Brain,
  PanelRightOpen,
  ArrowRight,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { api, Task, TaskBucket, TaskLink, TaskPriority } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import TaskDetailModal from "@/components/TaskDetailModal";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link as RouterLink } from "react-router-dom";

type TaskOwner = "arek" | "kasia";
type OwnerFilter = "all" | TaskOwner;

const OWNER_FILTERS: { id: OwnerFilter; label: string }[] = [
  { id: "all", label: "Wszyscy" },
  { id: "arek", label: "Arek" },
  { id: "kasia", label: "Kasia" },
];

const OWNER_CARD_STYLES: Record<TaskOwner, string> = {
  arek: "border-blue-200 bg-blue-50 hover:border-blue-300",
  kasia: "border-pink-200 bg-pink-50 hover:border-pink-300",
};

const PRIORITY_BADGE: Record<TaskPriority, string> = {
  P0: "bg-rose-100 text-rose-800",
  P1: "bg-amber-100 text-amber-900",
  P2: "bg-slate-200 text-slate-700",
};

const columns: { bucket: TaskBucket; title: string; short: string; accent: string; rail: string }[] = [
  {
    bucket: "backlog",
    title: "Backlog",
    short: "Backlog",
    accent: "text-slate-500",
    rail: "border-l-slate-300",
  },
  { bucket: "todo", title: "ToDo", short: "ToDo", accent: "text-slate-600", rail: "border-l-slate-400" },
  {
    bucket: "in_progress",
    title: "In Progress",
    short: "W toku",
    accent: "text-brand",
    rail: "border-l-brand",
  },
  { bucket: "done", title: "Done", short: "Done", accent: "text-emerald-700", rail: "border-l-emerald-600" },
];

const BUCKET_ORDER: TaskBucket[] = ["backlog", "todo", "in_progress", "done"];
const BUCKET_SET = new Set<string>(BUCKET_ORDER);
const BUCKET_LABEL: Record<TaskBucket, string> = {
  backlog: "Backlog",
  todo: "ToDo",
  in_progress: "W toku",
  done: "Done",
};

/** Puste kolumny (np. Backlog): pointer najpierw; karty wygrywaja z tlem kolumny. */
const kanbanCollision: CollisionDetection = (args) => {
  const preferCardOrColumn = (hits: ReturnType<typeof pointerWithin>) => {
    const cardHit = hits.find((c) => !BUCKET_SET.has(String(c.id)));
    if (cardHit) return [cardHit];
    const columnHit = hits.find((c) => BUCKET_SET.has(String(c.id)));
    if (columnHit) return [columnHit];
    return hits;
  };

  const pointerHits = pointerWithin(args);
  if (pointerHits.length > 0) return preferCardOrColumn(pointerHits);

  const rectHits = rectIntersection(args);
  if (rectHits.length > 0) return preferCardOrColumn(rectHits);

  return closestCorners(args);
};

function resolveOwner(task: Task): TaskOwner {
  if (task.owner === "kasia" || task.owner === "arek") return task.owner;
  const hint = `${task.agent || ""} ${task.title || ""} ${task.description || ""}`.toLowerCase();
  if (hint.includes("kasia") || /\bkasi\b/.test(hint)) return "kasia";
  if (hint.includes("arek")) return "arek";
  return "arek";
}

function resolvePriority(task: Task): TaskPriority {
  if (task.priority === "P0" || task.priority === "P1" || task.priority === "P2") {
    return task.priority;
  }
  return "P1";
}

function isDeadlineOverdue(deadline: string | null | undefined): boolean {
  if (!deadline) return false;
  const m = deadline.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return false;
  const end = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59);
  return end.getTime() < Date.now();
}

function useIsMobileMd(): boolean {
  const [mobile, setMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = () => setMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return mobile;
}

function nextBucket(bucket: TaskBucket): TaskBucket | null {
  const i = BUCKET_ORDER.indexOf(bucket);
  return i >= 0 && i < BUCKET_ORDER.length - 1 ? BUCKET_ORDER[i + 1] : null;
}

function prevBucket(bucket: TaskBucket): TaskBucket | null {
  const i = BUCKET_ORDER.indexOf(bucket);
  return i > 0 ? BUCKET_ORDER[i - 1] : null;
}

function isBucketId(id: string): id is TaskBucket {
  return BUCKET_SET.has(id);
}

function pickDocumentLink(links: TaskLink[]): TaskLink | null {
  if (!links.length) return null;
  const byLabel = links.find((l) => (l.label || "").toLowerCase().startsWith("dokument"));
  if (byLabel) return byLabel;
  const byPath = links.find((l) => /\/dokumenty\//i.test(l.url));
  return byPath || null;
}

function pickMapLink(links: TaskLink[]): TaskLink | null {
  if (!links.length) return null;
  const byLabel = links.find((l) => (l.label || "").toLowerCase().startsWith("mapa"));
  if (byLabel) return byLabel;
  const byPath = links.find((l) => /\/mapy\//i.test(l.url));
  return byPath || null;
}

function panelPathFromUrl(url: string): string | null {
  try {
    const u = new URL(url, typeof window !== "undefined" ? window.location.origin : "https://local");
    if (u.pathname.startsWith("/dokumenty/") || u.pathname.startsWith("/mapy/")) {
      return `${u.pathname}${u.search}${u.hash}`;
    }
  } catch {
    /* ignore */
  }
  return null;
}

const FLAT_RANK = {
  titleSizeRem: 0.95,
  titleColor: "rgb(30 41 59)",
  bodySizeRem: 0.8,
  bodyColor: "rgb(71 85 105)",
  metaSizeRem: 0.7,
  metaColor: "rgb(100 116 139)",
  padY: "0.65rem",
} as const;

/** Hierarchia: index 0 = 2x text-sm, nizej mniejszy i jasniejszy. Backlog = flat. */
function rankVisual(
  index: number,
  total: number,
  flat = false,
): {
  titleSizeRem: number;
  titleColor: string;
  bodySizeRem: number;
  bodyColor: string;
  metaSizeRem: number;
  metaColor: string;
  padY: string;
} {
  if (flat) return { ...FLAT_RANK };
  const t = total <= 1 ? 0 : Math.min(1, Math.max(0, index / (total - 1)));
  const titleSizeRem = 1.75 - t * 1.03;
  const bodySizeRem = 1.05 - t * 0.35;
  const metaSizeRem = 0.85 - t * 0.2;
  const dark = Math.round(15 + t * 120);
  const mid = Math.round(23 + t * 110);
  const light = Math.round(42 + t * 100);
  const titleColor = `rgb(${dark} ${mid} ${light})`;
  const bodyColor = `rgb(${Math.min(255, dark + 40)} ${Math.min(255, mid + 40)} ${Math.min(255, light + 40)})`;
  const metaColor = `rgb(${Math.min(255, dark + 70)} ${Math.min(255, mid + 65)} ${Math.min(255, light + 60)})`;
  const padY = t < 0.25 ? "0.85rem" : t < 0.6 ? "0.7rem" : "0.55rem";
  return { titleSizeRem, titleColor, bodySizeRem, bodyColor, metaSizeRem, metaColor, padY };
}

function applyOptimisticReorder(prev: Task[], bucket: TaskBucket, orderedIds: string[]): Task[] {
  const idSet = new Set(orderedIds);
  const byId = new Map(prev.map((t) => [t.id, t]));
  const moved = orderedIds
    .map((id) => byId.get(id))
    .filter((t): t is Task => Boolean(t))
    .map((t, i) => ({ ...t, bucket, sort_order: i }));

  const slots = prev.map((t, i) => (idSet.has(t.id) ? i : -1)).filter((i) => i >= 0);
  const need = moved.length - slots.length;
  for (let n = 0; n < need; n++) slots.push(prev.length + n);

  const result: Array<Task | undefined> = prev.map((t) => (idSet.has(t.id) ? undefined : { ...t }));
  while (result.length <= Math.max(-1, ...slots)) result.push(undefined);
  moved.forEach((t, i) => {
    result[slots[i]] = t;
  });
  return result.filter((t): t is Task => Boolean(t));
}

export default function Kanban() {
  const qc = useQueryClient();
  const isMobile = useIsMobileMd();
  const [ownerFilter, setOwnerFilter] = useState<OwnerFilter>("arek");
  const [newTitle, setNewTitle] = useState("");
  const [newOwner, setNewOwner] = useState<TaskOwner>("arek");
  const [activeId, setActiveId] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [mobileBucket, setMobileBucket] = useState<TaskBucket>("todo");

  const { data: tasks = [] } = useQuery({
    queryKey: ["tasks", ownerFilter],
    queryFn: () => api.listTasks(ownerFilter === "all" ? undefined : ownerFilter),
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const byBucket = useMemo(() => {
    const m: Record<TaskBucket, Task[]> = { backlog: [], todo: [], in_progress: [], done: [] };
    for (const t of tasks) m[t.bucket].push(t);
    return m;
  }, [tasks]);

  const detailTask = detailId ? tasks.find((t) => t.id === detailId) || null : null;

  const mMove = useMutation({
    mutationFn: ({ id, bucket }: { id: string; bucket: TaskBucket }) => api.moveTask(id, bucket),
    onMutate: async ({ id, bucket }) => {
      await qc.cancelQueries({ queryKey: ["tasks", ownerFilter] });
      const previous = qc.getQueryData<Task[]>(["tasks", ownerFilter]) || [];
      const maxOrder = previous
        .filter((t) => t.bucket === bucket)
        .reduce((m, t) => Math.max(m, t.sort_order ?? 0), -1);
      qc.setQueryData<Task[]>(
        ["tasks", ownerFilter],
        previous.map((t) =>
          t.id === id ? { ...t, bucket, sort_order: maxOrder + 1 } : t,
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(["tasks", ownerFilter], ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
  });
  const mReorder = useMutation({
    mutationFn: ({ bucket, orderedIds }: { bucket: TaskBucket; orderedIds: string[] }) =>
      api.reorderTasks(bucket, orderedIds),
    onMutate: async ({ bucket, orderedIds }) => {
      await qc.cancelQueries({ queryKey: ["tasks", ownerFilter] });
      const previous = qc.getQueryData<Task[]>(["tasks", ownerFilter]) || [];
      qc.setQueryData(["tasks", ownerFilter], applyOptimisticReorder(previous, bucket, orderedIds));
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(["tasks", ownerFilter], ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
  });
  const mCreate = useMutation({
    mutationFn: () =>
      api.createTask({
        title: newTitle,
        task_type: "human",
        bucket: "todo" as TaskBucket,
        owner: isMobile
          ? ownerFilter === "all"
            ? newOwner
            : ownerFilter
          : newOwner,
        priority: "P1",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
      setNewTitle("");
      if (isMobile) setMobileBucket("todo");
    },
  });

  const setPersonMobile = (owner: TaskOwner) => {
    setOwnerFilter(owner);
    setNewOwner(owner);
  };

  const reorderInBucket = (bucket: TaskBucket, orderedIds: string[]) => {
    if (orderedIds.length < 1) return;
    mReorder.mutate({ bucket, orderedIds });
  };

  const moveWithinMobile = (taskId: string, direction: "up" | "down") => {
    const list = byBucket[mobileBucket];
    const oldIndex = list.findIndex((t) => t.id === taskId);
    if (oldIndex < 0) return;
    const newIndex = direction === "up" ? oldIndex - 1 : oldIndex + 1;
    if (newIndex < 0 || newIndex >= list.length) return;
    reorderInBucket(
      mobileBucket,
      arrayMove(list, oldIndex, newIndex).map((t) => t.id),
    );
  };

  const onDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));
  const onDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;

    const taskId = String(active.id);
    const overId = String(over.id);
    if (taskId === overId) return;

    const activeTask = tasks.find((t) => t.id === taskId);
    if (!activeTask) return;

    const overBucket: TaskBucket | undefined = isBucketId(overId)
      ? overId
      : tasks.find((t) => t.id === overId)?.bucket;
    if (!overBucket) return;

    const sourceList = byBucket[activeTask.bucket];
    const destList = byBucket[overBucket];
    const oldIndex = sourceList.findIndex((t) => t.id === taskId);
    if (oldIndex < 0) return;

    if (activeTask.bucket === overBucket) {
      const newIndex = isBucketId(overId)
        ? destList.length - 1
        : destList.findIndex((t) => t.id === overId);
      if (newIndex < 0 || oldIndex === newIndex) return;
      reorderInBucket(
        overBucket,
        arrayMove(sourceList, oldIndex, newIndex).map((t) => t.id),
      );
      return;
    }

    // Pusta kolumna: move (reorder na pustej liscie bywal zawodny w detekcji dropu).
    if (destList.length === 0) {
      mMove.mutate({ id: taskId, bucket: overBucket });
      return;
    }

    if (isBucketId(overId)) {
      reorderInBucket(overBucket, [...destList.map((t) => t.id), taskId]);
      return;
    }

    const overIndex = destList.findIndex((t) => t.id === overId);
    const insertAt = overIndex < 0 ? destList.length : overIndex;
    const nextDest = destList.filter((t) => t.id !== taskId);
    nextDest.splice(insertAt, 0, activeTask);
    reorderInBucket(
      overBucket,
      nextDest.map((t) => t.id),
    );
  };

  const activeTask = tasks.find((t) => t.id === activeId);
  const busy = mMove.isPending || mReorder.isPending;

  const createForm = (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (newTitle.trim()) mCreate.mutate();
      }}
      className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center"
    >
      {!isMobile ? (
        <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-0.5">
          {(["arek", "kasia"] as TaskOwner[]).map((owner) => (
            <Button
              key={owner}
              type="button"
              size="sm"
              variant={newOwner === owner ? "default" : "ghost"}
              className={cn(
                "h-8 px-3",
                newOwner === owner && owner === "arek" && "bg-blue-600 hover:bg-blue-700",
                newOwner === owner && owner === "kasia" && "bg-pink-600 hover:bg-pink-700",
              )}
              onClick={() => setNewOwner(owner)}
            >
              {owner === "arek" ? "Arek" : "Kasia"}
            </Button>
          ))}
        </div>
      ) : null}
      <div className="flex min-w-0 flex-1 gap-2">
        <Input
          placeholder="Nowe zadanie…"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          className="min-w-0 flex-1 bg-white"
        />
        <Button type="submit" size="sm" variant="default" className="shrink-0">
          <Plus className="mr-1 h-3.5 w-3.5" /> Dodaj
        </Button>
      </div>
    </form>
  );

  return (
    <div className="mx-0 max-w-full overflow-x-hidden rounded-2xl border border-border bg-[#f7f8fa] p-3 text-foreground shadow-sm sm:p-6">
      <div className={cn(isMobile && "mb-3")}>
        <PageHeader
          title="Kanban"
          subtitle={isMobile ? undefined : "Zadania z bota Telegram + agenci AI"}
          actions={!isMobile ? createForm : undefined}
        />
      </div>

      {isMobile ? (
        <div className="sticky top-14 z-20 mb-3 w-full space-y-3 border-b border-slate-200 bg-[#f7f8fa] pb-3 pt-1">
          <div className="grid grid-cols-2 gap-1 rounded-xl border border-slate-200 bg-white p-1">
            {(["arek", "kasia"] as TaskOwner[]).map((owner) => (
              <Button
                key={owner}
                type="button"
                size="sm"
                variant={ownerFilter === owner ? "default" : "ghost"}
                className={cn(
                  "h-11 text-base font-semibold",
                  ownerFilter === owner && owner === "arek" && "bg-blue-600 hover:bg-blue-700",
                  ownerFilter === owner && owner === "kasia" && "bg-pink-600 hover:bg-pink-700",
                )}
                onClick={() => setPersonMobile(owner)}
              >
                {owner === "arek" ? "Arek" : "Kasia"}
              </Button>
            ))}
          </div>
          <button
            type="button"
            className={cn(
              "text-xs font-medium text-slate-500 underline-offset-2 hover:underline",
              ownerFilter === "all" && "text-slate-800",
            )}
            onClick={() => setOwnerFilter("all")}
          >
            {ownerFilter === "all" ? "Widok: wszyscy" : "Pokaż wszystkich"}
          </button>
          {createForm}
        </div>
      ) : (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Filtr</span>
          {OWNER_FILTERS.map((row) => (
            <Button
              key={row.id}
              size="sm"
              variant={ownerFilter === row.id ? "brand" : "outline"}
              className={cn(
                ownerFilter === row.id && row.id === "arek" && "border-blue-300 bg-blue-600 hover:bg-blue-700",
                ownerFilter === row.id && row.id === "kasia" && "border-pink-300 bg-pink-600 hover:bg-pink-700",
              )}
              onClick={() => setOwnerFilter(row.id)}
            >
              {row.label}
            </Button>
          ))}
        </div>
      )}

      {isMobile ? (
        <>
          <div className="mb-3 grid grid-cols-4 gap-1 rounded-xl border border-slate-200 bg-white p-1">
            {columns.map((col) => (
              <button
                key={col.bucket}
                type="button"
                className={cn(
                  "flex flex-col items-center rounded-lg px-0.5 py-2 text-[11px] font-semibold leading-tight transition-colors sm:text-xs",
                  mobileBucket === col.bucket
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-50",
                )}
                onClick={() => setMobileBucket(col.bucket)}
              >
                <span>{col.short}</span>
                <span
                  className={cn(
                    "mt-0.5 text-[10px] font-medium",
                    mobileBucket === col.bucket ? "text-white/80" : "text-slate-400",
                  )}
                >
                  {byBucket[col.bucket].length}
                </span>
              </button>
            ))}
          </div>
          <div className="space-y-2">
            {byBucket[mobileBucket].map((t, index) => (
              <TaskCard
                key={t.id}
                task={t}
                mobile
                flatRank={mobileBucket === "backlog"}
                rankIndex={index}
                rankTotal={byBucket[mobileBucket].length}
                onOpenDetails={setDetailId}
                onMove={(bucket) => mMove.mutate({ id: t.id, bucket })}
                onReorder={(direction) => moveWithinMobile(t.id, direction)}
                canMoveUp={index > 0}
                canMoveDown={index < byBucket[mobileBucket].length - 1}
                moving={busy}
              />
            ))}
            {byBucket[mobileBucket].length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 bg-white/70 py-12 text-center text-xs text-slate-500">
                Brak zadań w tej kolumnie
              </div>
            ) : null}
          </div>
        </>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={kanbanCollision}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <div className="grid gap-4 md:grid-cols-4">
            {columns.map((col) => (
              <Column
                key={col.bucket}
                {...col}
                items={byBucket[col.bucket]}
                flatRank={col.bucket === "backlog"}
                onOpenDetails={setDetailId}
              />
            ))}
          </div>
          <DragOverlay>
            {activeTask ? (
              <TaskCard
                task={activeTask}
                overlay
                flatRank={activeTask.bucket === "backlog"}
                rankIndex={0}
                rankTotal={Math.max(1, byBucket[activeTask.bucket].length)}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {detailTask ? (
        <TaskDetailModal task={detailTask} onClose={() => setDetailId(null)} />
      ) : null}
    </div>
  );
}

function Column({
  bucket,
  title,
  accent,
  rail,
  items,
  flatRank = false,
  onOpenDetails,
}: {
  bucket: TaskBucket;
  title: string;
  accent: string;
  rail: string;
  items: Task[];
  flatRank?: boolean;
  onOpenDetails: (id: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: bucket });
  const itemIds = items.map((t) => t.id);

  return (
    <Card
      ref={setNodeRef}
      className={cn(
        "rounded-xl border border-slate-200 bg-[#e8eaee] shadow-none backdrop-blur-none border-l-4",
        rail,
        isOver && "ring-2 ring-brand/40",
      )}
    >
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className={accent + " font-semibold"}>{title}</CardTitle>
        <Badge variant="secondary" className="bg-white text-slate-700">
          {items.length}
        </Badge>
      </CardHeader>
      <CardContent>
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          <div
            className={cn(
              "min-h-[320px] space-y-2 rounded-lg border border-dashed border-transparent transition-colors",
              isOver && "border-brand/50 bg-white/70",
            )}
          >
            {items.map((t, index) => (
              <TaskCard
                key={t.id}
                task={t}
                flatRank={flatRank}
                rankIndex={index}
                rankTotal={items.length}
                onOpenDetails={onOpenDetails}
              />
            ))}
            {items.length === 0 && (
              <div className="py-10 text-center text-xs text-slate-500">
                brak zadań. Przeciągnij tu albo dodaj nowe
              </div>
            )}
          </div>
        </SortableContext>
      </CardContent>
    </Card>
  );
}

function TaskCard({
  task,
  overlay = false,
  mobile = false,
  flatRank = false,
  rankIndex = 0,
  rankTotal = 1,
  onOpenDetails,
  onMove,
  onReorder,
  canMoveUp = false,
  canMoveDown = false,
  moving = false,
}: {
  task: Task;
  overlay?: boolean;
  mobile?: boolean;
  flatRank?: boolean;
  rankIndex?: number;
  rankTotal?: number;
  onOpenDetails?: (id: string) => void;
  onMove?: (bucket: TaskBucket) => void;
  onReorder?: (direction: "up" | "down") => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  moving?: boolean;
}) {
  const sortable = useSortable({ id: task.id, disabled: mobile || overlay });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;
  const SourceIcon = task.task_type === "human" ? User : Bot;
  const owner = resolveOwner(task);
  const ownerLabel = owner === "kasia" ? "Kasia" : "Arek";
  const priority = resolvePriority(task);
  const links = task.links || [];
  const docLink = pickDocumentLink(links);
  const docPath = docLink ? panelPathFromUrl(docLink.url) : null;
  const mapLink = pickMapLink(links.filter((l) => l !== docLink));
  const mapPath = mapLink ? panelPathFromUrl(mapLink.url) : null;
  const overdue = isDeadlineOverdue(task.deadline);
  const forward = nextBucket(task.bucket);
  const back = prevBucket(task.bucket);
  const rank = rankVisual(rankIndex, rankTotal, flatRank || task.bucket === "backlog");

  const stopDrag = (e: SyntheticEvent) => e.stopPropagation();

  const style: CSSProperties = {
    ...(mobile || overlay
      ? {}
      : {
          transform: CSS.Transform.toString(transform),
          transition,
          opacity: isDragging ? 0.4 : 1,
        }),
    paddingTop: rank.padY,
    paddingBottom: rank.padY,
  };

  return (
    <div
      ref={mobile || overlay ? undefined : setNodeRef}
      style={style}
      {...(mobile || overlay ? {} : listeners)}
      {...(mobile || overlay ? {} : attributes)}
      className={cn(
        "group rounded-md border px-3 text-slate-900",
        OWNER_CARD_STYLES[owner],
        !mobile && !overlay && "cursor-grab active:cursor-grabbing",
        overlay && "rotate-1 border-slate-300 shadow-lg",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        {onOpenDetails && !overlay ? (
          <button
            type="button"
            className="min-w-0 flex-1 text-left font-semibold leading-snug underline-offset-2 hover:underline"
            style={{ fontSize: `${rank.titleSizeRem}rem`, color: rank.titleColor }}
            onPointerDown={stopDrag}
            onClick={(e) => {
              e.stopPropagation();
              onOpenDetails(task.id);
            }}
          >
            {task.title}
          </button>
        ) : (
          <div
            className="min-w-0 flex-1 font-semibold leading-snug"
            style={{ fontSize: `${rank.titleSizeRem}rem`, color: rank.titleColor }}
          >
            {task.title}
          </div>
        )}
        <div className="flex shrink-0 items-center gap-1 pt-0.5">
          <Badge variant="secondary" className={cn("border-0 text-[10px] font-medium", PRIORITY_BADGE[priority])}>
            {priority}
          </Badge>
          <Badge
            variant="secondary"
            className={cn(
              "border-0 text-[10px] font-medium",
              owner === "arek" ? "bg-blue-100 text-blue-800" : "bg-pink-100 text-pink-800",
            )}
          >
            {ownerLabel}
          </Badge>
          {onOpenDetails ? (
            <button
              type="button"
              title="Szczegóły"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-white/80 hover:text-slate-800"
              onPointerDown={stopDrag}
              onClick={(e) => {
                e.stopPropagation();
                onOpenDetails(task.id);
              }}
            >
              <PanelRightOpen className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      </div>
      {docLink ? (
        docPath ? (
          <RouterLink
            to={docPath}
            className="mt-1 inline-flex max-w-full items-start gap-1.5 font-medium text-blue-600 underline-offset-2 hover:text-blue-700 hover:underline"
            style={{ fontSize: `${rank.bodySizeRem}rem` }}
            onPointerDown={stopDrag}
            onClick={(e) => e.stopPropagation()}
          >
            <FileText className="mt-[0.15em] h-[1em] w-[1em] shrink-0" aria-hidden />
            <span className="min-w-0 line-clamp-2 leading-snug">
              {docLink.label || task.description || "Otwórz dokument"}
            </span>
          </RouterLink>
        ) : (
          <a
            href={docLink.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex max-w-full items-start gap-1.5 font-medium text-blue-600 underline-offset-2 hover:text-blue-700 hover:underline"
            style={{ fontSize: `${rank.bodySizeRem}rem` }}
            onPointerDown={stopDrag}
            onClick={(e) => e.stopPropagation()}
          >
            <FileText className="mt-[0.15em] h-[1em] w-[1em] shrink-0" aria-hidden />
            <span className="min-w-0 line-clamp-2 leading-snug">
              {docLink.label || task.description || "Otwórz dokument"}
            </span>
          </a>
        )
      ) : null}
      {mapLink ? (
        mapPath ? (
          <RouterLink
            to={mapPath}
            className="mt-1 inline-flex max-w-full items-start gap-1.5 font-medium text-indigo-600 underline-offset-2 hover:text-indigo-700 hover:underline"
            style={{ fontSize: `${rank.bodySizeRem}rem` }}
            onPointerDown={stopDrag}
            onClick={(e) => e.stopPropagation()}
          >
            <Brain className="mt-[0.15em] h-[1em] w-[1em] shrink-0" aria-hidden />
            <span className="min-w-0 line-clamp-2 leading-snug">
              {mapLink.label || "Otwórz mapę myśli"}
            </span>
          </RouterLink>
        ) : (
          <a
            href={mapLink.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex max-w-full items-start gap-1.5 font-medium text-indigo-600 underline-offset-2 hover:text-indigo-700 hover:underline"
            style={{ fontSize: `${rank.bodySizeRem}rem` }}
            onPointerDown={stopDrag}
            onClick={(e) => e.stopPropagation()}
          >
            <Brain className="mt-[0.15em] h-[1em] w-[1em] shrink-0" aria-hidden />
            <span className="min-w-0 line-clamp-2 leading-snug">
              {mapLink.label || "Otwórz mapę myśli"}
            </span>
          </a>
        )
      ) : null}
      {!docLink && !mapLink && task.description ? (
        <div
          className="mt-1 line-clamp-2 leading-snug"
          style={{ fontSize: `${rank.bodySizeRem}rem`, color: rank.bodyColor }}
        >
          {task.description}
        </div>
      ) : null}
      <div
        className="mt-2 flex items-center gap-2"
        style={{ fontSize: `${rank.metaSizeRem}rem`, color: rank.metaColor }}
      >
        <SourceIcon className="h-3 w-3 shrink-0" />
        <span>{task.agent ?? task.task_type}</span>
        {links.length > 0 ? (
          docPath ? (
            <RouterLink
              to={docPath}
              className="inline-flex items-center gap-0.5 text-blue-600 underline-offset-2 hover:underline"
              title={docLink?.label || "Otwórz link"}
              onPointerDown={stopDrag}
              onClick={(e) => e.stopPropagation()}
            >
              <Link2 className="h-3 w-3" />
              {links.length}
            </RouterLink>
          ) : docLink ? (
            <a
              href={docLink.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 text-blue-600 underline-offset-2 hover:underline"
              title={docLink.label || "Otwórz link"}
              onPointerDown={stopDrag}
              onClick={(e) => e.stopPropagation()}
            >
              <Link2 className="h-3 w-3" />
              {links.length}
            </a>
          ) : (
            <span className="inline-flex items-center gap-0.5" title={`${links.length} link(ów)`}>
              <Link2 className="h-3 w-3" />
              {links.length}
            </span>
          )
        ) : null}
        {task.deadline ? (
          <span
            className={cn(
              "ml-auto inline-flex items-center gap-1",
              overdue ? "font-medium text-rose-700" : "",
            )}
          >
            <Clock className="h-3 w-3" /> {task.deadline}
          </span>
        ) : null}
      </div>
      {mobile && onMove ? (
        <div className="mt-3 space-y-2">
          {onReorder ? (
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-9 flex-1 bg-white"
                disabled={!canMoveUp || moving}
                onClick={() => onReorder("up")}
              >
                <ArrowUp className="mr-1 h-3.5 w-3.5" />
                Wyżej
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-9 flex-1 bg-white"
                disabled={!canMoveDown || moving}
                onClick={() => onReorder("down")}
              >
                Niżej
                <ArrowDown className="ml-1 h-3.5 w-3.5" />
              </Button>
            </div>
          ) : null}
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-9 flex-1 bg-white"
              disabled={!back || moving}
              onClick={() => back && onMove(back)}
            >
              <ArrowLeft className="mr-1 h-3.5 w-3.5" />
              {back ? BUCKET_LABEL[back] : "Wstecz"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-9 flex-1 bg-white"
              disabled={!forward || moving}
              onClick={() => forward && onMove(forward)}
            >
              {forward ? BUCKET_LABEL[forward] : "Dalej"}
              <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
